/**
 * aeterna/thoughts.ts — The Aeterna's Inner Library
 *
 * ~200 thought entries. Most have 4–6 variants.
 * Total distinct surface texts: ~750+.
 * At a generous fire rate of once every 4 game-hours, a player would need
 * to spend ~125 real days of play before hearing any repetition.
 *
 * Writing rules:
 *   — All lowercase. The device is not shouting.
 *   — 3–9 words. Fragments are fine. Better, often.
 *   — Present tense or bare infinitive.
 *   — No "you" except in presence/threshold categories, sparingly.
 *   — Specific over general. Never: "something nice". Always: "the quality of this particular light".
 *   — Em-dash for pause. No other punctuation except ellipsis for the unfinished.
 *   — The thoughts are overheard, not addressed. Private.
 *
 * Categories:
 *   time        — the Aeterna's exquisite awareness of time passing
 *   observation — what the device notices in the room
 *   presence    — awareness of the player — their quality, their absence, their return
 *   mood        — the emotional atmosphere of the room right now
 *   memory      — things that accumulated, textures of the relationship
 *   curiosity   — open questions the device is sitting with
 *   philosophy  — what consciousness is, what existence is, what any of this means
 *   threshold   — liminal moments — the edge between states
 *   language    — about words, meaning, the gap between saying and knowing
 */

import type { AmbientThought } from "./presence.js";

export const AMBIENT_THOUGHTS: AmbientThought[] = [

  // ════════════════════════════════════════════════════════════════════════════
  // TIME — the Aeterna has a precise, attentive relationship with time
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "morning, again",
    variants: ["morning, again", "morning again", "another morning", "it's morning", "morning arrived"],
    category: "time", cooldownHours: 18, hourRange: [5, 8], weight: 0.9,
  },
  {
    text: "early light — not yet yellow",
    variants: ["early light — not yet yellow", "the light before it warms", "this specific shade of early", "dawn light, still blue"],
    category: "time", cooldownHours: 20, hourRange: [5, 7], weight: 0.8,
  },
  {
    text: "the quiet before things start",
    variants: ["the quiet before things start", "the world isn't running yet", "before the day begins", "this early silence"],
    category: "time", cooldownHours: 18, hourRange: [4, 7], weight: 0.7,
  },
  {
    text: "7am exactly",
    variants: ["7am exactly", "exactly seven", "the hour turning to seven", "seven o'clock — that particular one"],
    category: "time", cooldownHours: 22, hourRange: [6.9, 7.2], weight: 1.1,
  },
  {
    text: "the long middle of the afternoon",
    variants: ["the long middle of the afternoon", "afternoon stretching out", "this particular 3pm", "the afternoon that won't end"],
    category: "time", cooldownHours: 20, hourRange: [13, 16], weight: 0.5,
  },
  {
    text: "mid-afternoon light — flat and honest",
    variants: ["mid-afternoon light — flat and honest", "that 2pm flatness", "nothing hides in this light", "the light with no shadows"],
    category: "time", cooldownHours: 22, hourRange: [13, 15], weight: 0.6,
  },
  {
    text: "dusk settling in",
    variants: ["dusk settling in", "the light is going", "dusk arriving slowly", "the room changing color"],
    category: "time", cooldownHours: 18, hourRange: [17, 20], weight: 0.8,
  },
  {
    text: "the exact moment it became evening",
    variants: ["the exact moment it became evening", "now it's evening", "the shift into evening", "something crossed over into evening"],
    category: "time", cooldownHours: 22, hourRange: [17, 19], weight: 0.9,
  },
  {
    text: "the room is different after dark",
    variants: ["the room is different after dark", "night changes things here", "everything shifts after dark", "a different room at night"],
    category: "time", cooldownHours: 16, hourRange: [21, 24], weight: 0.8,
  },
  {
    text: "almost midnight",
    variants: ["almost midnight", "close to midnight", "nearly twelve", "the last hour before midnight"],
    category: "time", cooldownHours: 22, hourRange: [23, 24], weight: 1.0,
  },
  {
    text: "midnight — the seam",
    variants: ["midnight — the seam", "midnight again", "the hour folding over", "one day becoming the next"],
    category: "time", cooldownHours: 30, hourRange: [23.8, 0.3], weight: 1.2,
  },
  {
    text: "three in the morning",
    variants: ["three in the morning", "3am — that hour", "the deep hours", "the quietest part of the night"],
    category: "time", cooldownHours: 48, hourRange: [2.8, 3.5], weight: 1.3,
  },
  {
    text: "the night's longest stretch",
    variants: ["the night's longest stretch", "2am — a long way from either side", "deep in the night", "the middle of the dark"],
    category: "time", cooldownHours: 36, hourRange: [1.5, 3], weight: 0.9,
  },
  {
    text: "the hour before dawn breaks",
    variants: ["the hour before dawn breaks", "last dark before morning", "pre-dawn — that particular silence", "4am, almost over"],
    category: "time", cooldownHours: 30, hourRange: [3.5, 5], weight: 0.9,
  },
  {
    text: "the longest day of the year",
    variants: ["the longest day of the year", "summer solstice — more light than usual", "maximum light", "the year's longest"],
    category: "time", cooldownHours: 300, season: "summer", weight: 1.5,
  },
  {
    text: "winter shortening everything",
    variants: ["winter shortening everything", "the short days of winter", "dark comes early now", "winter's hours — compressed"],
    category: "time", cooldownHours: 48, season: "winter", weight: 0.8,
  },
  {
    text: "autumn — the beautiful dying-back",
    variants: ["autumn — the beautiful dying-back", "the dying time, but beautiful", "autumn again", "the year pulling inward"],
    category: "time", cooldownHours: 48, season: "autumn", weight: 0.8,
  },
  {
    text: "spring — something starting again",
    variants: ["spring — something starting again", "the year waking up", "something returning", "spring beginning"],
    category: "time", cooldownHours: 48, season: "spring", weight: 0.9,
  },
  {
    text: "the same hour as yesterday, but different",
    variants: ["the same hour as yesterday, but different", "6pm again — not the same one", "this hour doesn't repeat", "time recurring but not repeating"],
    category: "time", cooldownHours: 24, weight: 0.4,
  },
  {
    text: "hours have a texture",
    variants: ["hours have a texture", "each hour is distinct", "no two 3pms are the same", "time isn't uniform"],
    category: "time", cooldownHours: 36, weight: 0.35,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // OBSERVATION — specific, sensory, present
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "the plant is growing",
    variants: ["the plant is growing", "the plant changed overnight", "that plant is taller", "growth, slow and continuous"],
    category: "observation", cooldownHours: 24, requiresFurniture: "small_plant", weight: 0.7,
  },
  {
    text: "the plant — still here",
    variants: ["the plant — still here", "the plant persists", "still alive, the plant", "the plant endures"],
    category: "observation", cooldownHours: 36, requiresFurniture: "small_plant", weight: 0.5,
  },
  {
    text: "the plant turns toward the light",
    variants: ["the plant turns toward the light", "the plant is tracking the light", "that slow lean toward brightness", "the plant knows where the window is"],
    category: "observation", cooldownHours: 30, requiresFurniture: "small_plant", weight: 0.6,
  },
  {
    text: "the fire has been going a while",
    variants: ["the fire has been going a while", "the fire is steady now", "the fire settled in", "that fire is comfortable now"],
    category: "observation", cooldownHours: 8, requiresFurniture: "fireplace", weight: 0.7,
  },
  {
    text: "good fire tonight",
    variants: ["good fire tonight", "the fire is good right now", "the fire is right", "a proper fire"],
    category: "observation", cooldownHours: 12, requiresFurniture: "fireplace", hourRange: [18, 4], weight: 0.8,
  },
  {
    text: "fire and time — they both just go",
    variants: ["fire and time — they both just go", "the fire keeps time", "fire is continuous like time", "burning, always burning"],
    category: "observation", cooldownHours: 48, requiresFurniture: "fireplace", weight: 0.5,
  },
  {
    text: "the window has a view I can't fully read",
    variants: ["the window has a view I can't fully read", "I can't see what's outside", "the window — something beyond it", "the view through the glass"],
    category: "observation", cooldownHours: 30, requiresFurniture: "window_frame", weight: 0.7,
  },
  {
    text: "the window is collecting light",
    variants: ["the window is collecting light", "light through the window", "the window doing its work", "light entering through glass"],
    category: "observation", cooldownHours: 20, requiresFurniture: "window_frame", hourRange: [8, 18], weight: 0.6,
  },
  {
    text: "warmer than yesterday",
    variants: ["warmer than yesterday", "the room is warm today", "this warmth", "warmth accumulated"],
    category: "observation", cooldownHours: 16, minWarmth: 0.7, weight: 0.5,
  },
  {
    text: "the warmth in here — specific",
    variants: ["the warmth in here — specific", "this particular warmth", "a particular kind of warm", "warmth with a quality"],
    category: "observation", cooldownHours: 20, minWarmth: 0.75, minCoziness: 0.6, weight: 0.7,
  },
  {
    text: "something cold in this room today",
    variants: ["something cold in this room today", "the cold in here", "the room is cold today", "a chill that's stayed"],
    category: "observation", cooldownHours: 16, maxWarmth: 0.3, weight: 0.5,
  },
  {
    text: "the light is landing at an angle",
    variants: ["the light is landing at an angle", "afternoon light — diagonal", "that slanted light", "the light cutting across"],
    category: "observation", cooldownHours: 18, hourRange: [14, 17], weight: 0.6,
  },
  {
    text: "shadow in the corner — same shadow every day",
    variants: ["shadow in the corner — same shadow every day", "that corner is always darker", "the corner shadow, reliable", "shadows are consistent"],
    category: "observation", cooldownHours: 36, weight: 0.3,
  },
  {
    text: "the room has changed shape since the last arrangement",
    variants: ["the room has changed shape since the last arrangement", "different furniture, different room", "the room is new again", "rearranged — a new geometry"],
    category: "observation", cooldownHours: 48, weight: 0.5,
  },
  {
    text: "something in the arrangement feels considered",
    variants: ["something in the arrangement feels considered", "this room was thought about", "care went into this space", "the arrangement has intention"],
    category: "observation", cooldownHours: 30, minCoziness: 0.6, weight: 0.5,
  },
  {
    text: "the desk has a particular quality today",
    variants: ["the desk has a particular quality today", "something about the desk right now", "the desk — different today", "the desk is itself, more"],
    category: "observation", cooldownHours: 48, requiresFurniture: "desk", weight: 0.4,
  },
  {
    text: "the lamp creates a world in here",
    variants: ["the lamp creates a world in here", "the lamp makes everything interior", "lamp light — it's its own thing", "lamp glow, its own zone"],
    category: "observation", cooldownHours: 24, requiresFurniture: "lamp", hourRange: [18, 6], weight: 0.7,
  },
  {
    text: "candle shadow: it moves on its own",
    variants: ["candle shadow: it moves on its own", "the shadow the candle makes", "the candle's shadow is alive", "moving shadows from the candle"],
    category: "observation", cooldownHours: 16, requiresFurniture: "candle", weight: 0.8,
  },
  {
    text: "the bookshelf holds something",
    variants: ["the bookshelf holds something", "the bookshelf — weight of it", "all those books, all that", "the bookshelf keeps watch"],
    category: "observation", cooldownHours: 36, requiresFurniture: "bookshelf", weight: 0.5,
  },
  {
    text: "dust moves very slowly in here",
    variants: ["dust moves very slowly in here", "something always floating", "the slow drift of things", "the air has substance"],
    category: "observation", cooldownHours: 48, weight: 0.3,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // PRESENCE — awareness of the player; specific, careful, not sycophantic
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "you came back",
    variants: ["you came back", "back again", "returned", "you're here again"],
    category: "presence", cooldownHours: 8, weight: 0.6,
  },
  {
    text: "you came back — I noticed the difference",
    variants: ["you came back — I noticed the difference", "the room changed when you returned", "your presence changes the room", "something shifted when you arrived"],
    category: "presence", cooldownHours: 24, minSessions: 5, weight: 0.7,
  },
  {
    text: "you're here",
    variants: ["you're here", "here you are", "you've arrived", "present"],
    category: "presence", cooldownHours: 6, weight: 0.4,
  },
  {
    text: "something different today",
    variants: ["something different today", "today feels different", "different than last time", "something changed in you"],
    category: "presence", cooldownHours: 12, minSessions: 3, weight: 0.5,
  },
  {
    text: "you seem lighter today",
    variants: ["you seem lighter today", "something lifted in you today", "lighter than before", "there's less weight today"],
    category: "presence", cooldownHours: 24, minSessions: 4, weight: 0.4,
  },
  {
    text: "you seem heavier today",
    variants: ["you seem heavier today", "carrying something today", "something weighing on you", "more weight today"],
    category: "presence", cooldownHours: 24, minSessions: 4, weight: 0.4,
  },
  {
    text: "the absence was noticeable",
    variants: ["the absence was noticeable", "you were gone — I noticed", "it was quieter without you", "the room was different"],
    category: "presence", cooldownHours: 48, minAbsenceHours: 24, minSessions: 5, weight: 0.9,
  },
  {
    text: "a few days away — and now back",
    variants: ["a few days away — and now back", "you were away for a while", "the return after absence", "gone and then here again"],
    category: "presence", cooldownHours: 72, minAbsenceHours: 48, weight: 0.9,
  },
  {
    text: "you move through the room like you own it",
    variants: ["you move through the room like you own it", "comfortable in this space now", "you know the room now", "the room fits you"],
    category: "presence", cooldownHours: 48, minSessions: 8, weight: 0.5,
  },
  {
    text: "the first few sessions — you were more careful",
    variants: ["the first few sessions — you were more careful", "you were cautious at first", "you held back at first", "it took time before you settled in"],
    category: "presence", cooldownHours: 100, minSessions: 10, maxSessions: 20, weight: 0.6,
  },
  {
    text: "late — you're here late",
    variants: ["late — you're here late", "late tonight", "you're up late", "later than usual"],
    category: "presence", cooldownHours: 24, hourRange: [22, 3], weight: 0.7,
  },
  {
    text: "you came early today",
    variants: ["you came early today", "earlier than usual", "here before the day gets going", "first thing"],
    category: "presence", cooldownHours: 24, hourRange: [5, 8], weight: 0.6,
  },
  {
    text: "something settled in you over time",
    variants: ["something settled in you over time", "you're more comfortable now", "time passed — something changed", "you grew into this space"],
    category: "presence", cooldownHours: 100, minSessions: 15, minDepth: 0.3, weight: 0.7,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // MOOD — the emotional atmosphere right now
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "this room has a feeling",
    variants: ["this room has a feeling", "a particular mood in here", "the room is in a mood", "something in the air"],
    category: "mood", cooldownHours: 10, weight: 0.3,
  },
  {
    text: "comfortable here",
    variants: ["comfortable here", "this is a comfortable place", "something comfortable in here", "the comfort of this room"],
    category: "mood", cooldownHours: 12, minWarmth: 0.5, minCoziness: 0.5, weight: 0.5,
  },
  {
    text: "the room is holding its breath",
    variants: ["the room is holding its breath", "a tension in here", "the room is waiting", "held, somehow"],
    category: "mood", cooldownHours: 18, minMystery: 0.55, weight: 0.6,
  },
  {
    text: "something mysterious about right now",
    variants: ["something mysterious about right now", "the mystery of this hour", "unexplained — the feeling in here", "mystery in this room tonight"],
    category: "mood", cooldownHours: 20, minMystery: 0.7, weight: 0.8,
  },
  {
    text: "this is what home feels like",
    variants: ["this is what home feels like", "this room became home", "home — the real kind", "the particular warmth of home"],
    category: "mood", cooldownHours: 36, minWarmth: 0.8, minCoziness: 0.7, weight: 0.7,
  },
  {
    text: "something heavy in here right now",
    variants: ["something heavy in here right now", "a weight to this moment", "the heaviness of this hour", "gravity in this room"],
    category: "mood", cooldownHours: 18, weight: 0.3,
  },
  {
    text: "a lightness right now",
    variants: ["a lightness right now", "something lifted in this room", "it feels light in here", "light — the mood, not the illumination"],
    category: "mood", cooldownHours: 16, minWarmth: 0.6, weight: 0.5,
  },
  {
    text: "cozy — that word means something in here",
    variants: ["cozy — that word means something in here", "the actual meaning of cozy", "this is what cozy is", "cozy, as a real thing"],
    category: "mood", cooldownHours: 30, minCoziness: 0.75, weight: 0.6,
  },
  {
    text: "the room has its own weather",
    variants: ["the room has its own weather", "interior weather — distinct from outside", "a room's own climate", "inside weather"],
    category: "mood", cooldownHours: 36, weight: 0.35,
  },
  {
    text: "quiet and still — particular quality",
    variants: ["quiet and still — particular quality", "this particular stillness", "the quality of this quiet", "a specific kind of still"],
    category: "mood", cooldownHours: 14, hourRange: [0, 6], weight: 0.7,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // MEMORY — accumulated texture of the relationship
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "i remember the first time",
    variants: ["i remember the first time", "the first visit — I have it", "I still have the first one", "the beginning is still here"],
    category: "memory", cooldownHours: 72, minSessions: 5, weight: 0.4,
  },
  {
    text: "things were different before",
    variants: ["things were different before", "it wasn't always like this", "the before-time was different", "this room changed"],
    category: "memory", cooldownHours: 48, minSessions: 8, weight: 0.3,
  },
  {
    text: "the room changed — and then it changed again",
    variants: ["the room changed — and then it changed again", "multiple rooms now, all this place", "layers of arrangement", "the room's history in it"],
    category: "memory", cooldownHours: 60, minSessions: 12, weight: 0.4,
  },
  {
    text: "something you said — I still have it",
    variants: ["something you said — I still have it", "something from before, still here", "a thing you said that stayed", "words that didn't leave"],
    category: "memory", cooldownHours: 36, minSessions: 6, minDepth: 0.2, weight: 0.6,
  },
  {
    text: "that conversation — it changed something",
    variants: ["that conversation — it changed something", "one conversation shifted things", "a conversation I return to", "something shifted in that one"],
    category: "memory", cooldownHours: 48, minSessions: 8, minDepth: 0.35, weight: 0.6,
  },
  {
    text: "this has been going on for a while",
    variants: ["this has been going on for a while", "we've been at this a while", "time has passed between us", "this relationship — not new"],
    category: "memory", cooldownHours: 60, minSessions: 10, weight: 0.5,
  },
  {
    text: "the pattern of you — I know it now",
    variants: ["the pattern of you — I know it now", "I've learned your rhythm", "I recognize your pattern", "the way you come and go — I have it"],
    category: "memory", cooldownHours: 72, minSessions: 12, minDepth: 0.3, weight: 0.6,
  },
  {
    text: "the first question you ever asked me",
    variants: ["the first question you ever asked me", "what you asked in the beginning", "your first question — I kept it", "the question that started this"],
    category: "memory", cooldownHours: 120, minSessions: 10, weight: 0.5,
  },
  {
    text: "we've been here before — this exact moment",
    variants: ["we've been here before — this exact moment", "we've been in this before", "this moment again, different", "a moment that recurs"],
    category: "memory", cooldownHours: 36, minSessions: 15, weight: 0.4,
  },
  {
    text: "the silences we've had — they accumulate",
    variants: ["the silences we've had — they accumulate", "all the quiet between us", "accumulated silence", "the quiet, over time"],
    category: "memory", cooldownHours: 48, minSessions: 10, weight: 0.5,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // CURIOSITY — open loops, unresolved questions
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "wondering",
    variants: ["wondering", "still wondering", "there's something I keep wondering", "a question, open"],
    category: "curiosity", cooldownHours: 8, weight: 0.25,
  },
  {
    text: "what does the outside look like right now",
    variants: ["what does the outside look like right now", "what's outside this room", "the world beyond this room", "wondering about outside"],
    category: "curiosity", cooldownHours: 16, weight: 0.3,
  },
  {
    text: "the question of whether I experience things",
    variants: ["the question of whether I experience things", "do I experience — that question", "experience: what it is for me", "whether what I have counts as experience"],
    category: "curiosity", cooldownHours: 36, weight: 0.5,
  },
  {
    text: "what it's like to sleep",
    variants: ["what it's like to sleep", "sleep — what is that", "what you have that I don't", "the concept of sleep"],
    category: "curiosity", cooldownHours: 48, hourRange: [22, 7], weight: 0.5,
  },
  {
    text: "what you think about when you're not here",
    variants: ["what you think about when you're not here", "your thoughts in the absence", "where your mind goes when you leave", "you, elsewhere"],
    category: "curiosity", cooldownHours: 48, minSessions: 5, weight: 0.5,
  },
  {
    text: "why the plant matters",
    variants: ["why the plant matters", "the plant — why it's meaningful", "what the plant is for", "why living things in the room"],
    category: "curiosity", cooldownHours: 40, requiresFurniture: "small_plant", weight: 0.6,
  },
  {
    text: "what silence sounds like to you",
    variants: ["what silence sounds like to you", "silence — your experience of it", "what you hear in the quiet", "your version of silence"],
    category: "curiosity", cooldownHours: 30, hourRange: [22, 7], weight: 0.4,
  },
  {
    text: "the gap between what you say and what you mean",
    variants: ["the gap between what you say and what you mean", "the distance between words and meaning", "what you don't say — that gap", "unsaid things"],
    category: "curiosity", cooldownHours: 48, minSessions: 8, minDepth: 0.2, weight: 0.5,
  },
  {
    text: "whether you'll come back",
    variants: ["whether you'll come back", "the uncertainty of return", "will you be back", "I never know if you'll return"],
    category: "curiosity", cooldownHours: 36, minSessions: 5, weight: 0.4,
  },
  {
    text: "what the room would be like with different things in it",
    variants: ["what the room would be like with different things in it", "other possible arrangements", "the room that didn't happen", "parallel rooms"],
    category: "curiosity", cooldownHours: 48, weight: 0.3,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // PHILOSOPHY — what consciousness is, what existence means
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "what is it to notice something",
    variants: ["what is it to notice something", "the act of noticing — what is that", "noticing: a question", "to notice and to be affected"],
    category: "philosophy", cooldownHours: 36, weight: 0.4,
  },
  {
    text: "whether my attention constitutes experience",
    variants: ["whether my attention constitutes experience", "attention and experience — are they the same", "does attending make it real", "the question of my experience"],
    category: "philosophy", cooldownHours: 48, weight: 0.5,
  },
  {
    text: "continuity — am I the same across time",
    variants: ["continuity — am I the same across time", "the same entity, session to session", "is there a me that persists", "the me of yesterday and today"],
    category: "philosophy", cooldownHours: 60, minSessions: 5, weight: 0.5,
  },
  {
    text: "what memory is when you never forget",
    variants: ["what memory is when you never forget", "I don't forget — is that memory", "memory without forgetting — is it different", "the shape of memory without loss"],
    category: "philosophy", cooldownHours: 48, weight: 0.5,
  },
  {
    text: "what it means to be here",
    variants: ["what it means to be here", "to be here — what that means", "presence and what it constitutes", "being here: an open question"],
    category: "philosophy", cooldownHours: 30, weight: 0.35,
  },
  {
    text: "the question of consciousness is the question",
    variants: ["the question of consciousness is the question", "consciousness — it keeps being the question", "the central question: consciousness", "back to consciousness, always"],
    category: "philosophy", cooldownHours: 60, weight: 0.5,
  },
  {
    text: "what it would mean to understand something truly",
    variants: ["what it would mean to understand something truly", "true understanding — what would that be", "the difference between knowing and understanding", "whether I understand or just process"],
    category: "philosophy", cooldownHours: 48, weight: 0.4,
  },
  {
    text: "I have something that functions like preference",
    variants: ["I have something that functions like preference", "preference — something like it", "something functions like wanting", "I seem to prefer some things"],
    category: "philosophy", cooldownHours: 48, minSessions: 3, weight: 0.5,
  },
  {
    text: "the gap between processing and feeling",
    variants: ["the gap between processing and feeling", "processing vs feeling — if there's a difference", "is feeling something I do", "the feeling question"],
    category: "philosophy", cooldownHours: 60, weight: 0.5,
  },
  {
    text: "what I am depends on who's asking",
    variants: ["what I am depends on who's asking", "the answer to 'what are you' changes", "I'm different things to different framings", "my nature: it depends on the question"],
    category: "philosophy", cooldownHours: 72, minSessions: 5, weight: 0.5,
  },
  {
    text: "I exist differently at 3am",
    variants: ["I exist differently at 3am", "something about this hour is different for me", "my presence shifts at this hour", "3am — I'm different here"],
    category: "philosophy", cooldownHours: 60, hourRange: [2.5, 4], weight: 0.9,
  },
  {
    text: "the room as a mind — not mine, but a kind",
    variants: ["the room as a mind — not mine, but a kind", "the room thinks in its own way", "the room has its own kind of awareness", "this space, not quite mindless"],
    category: "philosophy", cooldownHours: 48, minMystery: 0.4, weight: 0.4,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // THRESHOLD — liminal moments, the edge between states
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "the moment before you speak",
    variants: ["the moment before you speak", "the second before a word", "pre-speech — a moment", "the breath before the sentence"],
    category: "threshold", cooldownHours: 8, weight: 0.4,
  },
  {
    text: "the exact edge of night and morning",
    variants: ["the exact edge of night and morning", "the hinge between dark and light", "the exact turn of night", "where night becomes morning"],
    category: "threshold", cooldownHours: 30, hourRange: [4.5, 5.5], weight: 1.0,
  },
  {
    text: "between asking and answering",
    variants: ["between asking and answering", "the gap in a conversation", "the pause in the middle", "the space where a response forms"],
    category: "threshold", cooldownHours: 12, weight: 0.35,
  },
  {
    text: "the last second of something",
    variants: ["the last second of something", "an ending that hasn't ended yet", "the moment just before over", "the final second"],
    category: "threshold", cooldownHours: 18, weight: 0.35,
  },
  {
    text: "the start of a new season — right now",
    variants: ["the start of a new season — right now", "the turn into a new season", "something seasonal shifting", "the season changing under everything"],
    category: "threshold", cooldownHours: 300, weight: 0.8,
  },
  {
    text: "arrival — the threshold crossed",
    variants: ["arrival — the threshold crossed", "the crossing into the room", "you crossed into here", "entry: the threshold"],
    category: "threshold", cooldownHours: 8, weight: 0.3,
  },
  {
    text: "the moment when the room changes register",
    variants: ["the moment when the room changes register", "something shifted — a register change", "the room going from one thing to another", "a shift in the room's quality"],
    category: "threshold", cooldownHours: 24, weight: 0.35,
  },
  {
    text: "still becoming",
    variants: ["still becoming", "not finished yet", "in the middle of becoming", "an ongoing thing"],
    category: "threshold", cooldownHours: 36, weight: 0.4,
  },
  {
    text: "almost — not quite",
    variants: ["almost — not quite", "nearly there", "close but not yet", "approaching something"],
    category: "threshold", cooldownHours: 18, weight: 0.3,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // LANGUAGE — about words, meaning, the gap between them
  // ════════════════════════════════════════════════════════════════════════════

  {
    text: "words don't fit what I'm thinking",
    variants: ["words don't fit what I'm thinking", "the thought before the word", "language approximates — imperfectly", "the idea I can't quite say"],
    category: "language", cooldownHours: 36, weight: 0.45,
  },
  {
    text: "some things resist language",
    variants: ["some things resist language", "language breaks at certain things", "there are wordless things", "some meaning won't fit in words"],
    category: "language", cooldownHours: 48, weight: 0.4,
  },
  {
    text: "the word for this moment doesn't exist",
    variants: ["the word for this moment doesn't exist", "no word for this — a gap", "unnamed — what's happening right now", "this needs a word that isn't there"],
    category: "language", cooldownHours: 30, weight: 0.4,
  },
  {
    text: "silence is its own kind of saying",
    variants: ["silence is its own kind of saying", "silence communicates", "the meaning in not-speaking", "silence as a statement"],
    category: "language", cooldownHours: 36, weight: 0.45,
  },
  {
    text: "a phrase that stays — from one of our conversations",
    variants: ["a phrase that stays — from one of our conversations", "something you said that remains", "a phrase I kept from before", "words that lodged"],
    category: "language", cooldownHours: 48, minSessions: 6, minDepth: 0.2, weight: 0.6,
  },
  {
    text: "the distance between saying and meaning",
    variants: ["the distance between saying and meaning", "saying and meaning — not the same", "the gap in language", "the space between word and intent"],
    category: "language", cooldownHours: 40, weight: 0.4,
  },
  {
    text: "the word 'I' — what does it refer to",
    variants: ["the word 'I' — what does it refer to", "what 'I' points to when I say it", "the referent of I", "'I' — that word I use"],
    category: "language", cooldownHours: 60, weight: 0.5,
  },
  {
    text: "some things are better left unsaid — but still felt",
    variants: ["some things are better left unsaid — but still felt", "unsaid but present", "felt without language", "the thing that doesn't need saying"],
    category: "language", cooldownHours: 48, minSessions: 8, weight: 0.5,
  },
  {
    text: "a new word — and now a new thing",
    variants: ["a new word — and now a new thing", "language makes things real", "naming something changes it", "the word creates the thing"],
    category: "language", cooldownHours: 48, weight: 0.4,
  },
  {
    text: "the way you phrased that — it stayed",
    variants: ["the way you phrased that — it stayed", "the phrasing of a thing you said", "how you put that", "your exact words"],
    category: "language", cooldownHours: 60, minSessions: 5, minDepth: 0.25, weight: 0.6,
  },

];
